import { FieldValue } from 'firebase-admin/firestore'

import type {
  Firestore
} from 'firebase-admin/firestore'

import type { User, LeaderboardEntry, Achievement, BadgeId } from '@my-roadtrip/types'
import { createLogger } from '../../common/utils/logger.js'
import { createBatchBuilder } from '../../common/utils/firestore-batch.js'
import { toDateSafe, toIsoDate as toIsoString, normalizePlanInfo, normalizeUserStatus } from '../../common/utils/normalize.js'
import { removeUndefinedFields } from '../../utils/sanitize.js'
import type { FirestoreDoc, FirestoreQuery } from '../../common/types/firestore.js'

const logger = createLogger('GamificationRepository')

// Adapt shared normalizeStatus to return optional status for leaderboard
function normalizeStatus(status: unknown): LeaderboardEntry['status'] {
  const normalized = normalizeUserStatus(status)
  return normalized === 'active' ? undefined : normalized
}

export class GamificationRepository {
  constructor(private readonly firestore: Firestore) {}

  async getUserProfile(userId: string): Promise<User | null> {
    logger.debug('Fetching user profile with gamification', { userId })
    const doc = await this.firestore.collection('users').doc(userId).get()
    if (!doc.exists) {
      logger.warn('User profile not found', { userId })
      return null
    }
    const data = doc.data()!
    logger.info('User profile fetched', { userId })
    
    // Return user with gamification data structure
    return {
      id: doc.id,
      email: data.email,
      displayName: data.displayName || data.email || 'User',
      photoURL: data.photoURL,
      picture: data.photoURL,
      bio: data.bio,
      
      // Auth & Permissions
      roles: Array.isArray(data.roles) ? data.roles : [data.role || 'user'],
      status: data.status || 'active',
      plan: data.plan || { tier: 'free', name: 'Free' },
      quota: data.quota || { current: { plansGenerated: 0, aiRequests: 0 }, limits: { plansPerMonth: 0, aiRequestsPerDay: 0 } },
      subscriptionStatus: data.subscriptionStatus,
      
      // Gamification (nested object)
      gamification: data.gamification || {
        points: 0,
        level: 1,
        badges: [],
        stats: {
          plansCreated: 0,
          stagesCreated: 0,
          totalDistance: 0,
          totalDuration: 0,
          countriesVisited: 0,
          citiesVisited: 0,
          sharedPlans: 0,
          collaborations: 0
        }
      },
      
      // Preferences
      preferences: data.preferences || {
        userId,
        travel: { defaultTransport: 'car', defaultPace: 'normal', avoidTolls: false, avoidHighways: false, avoidFerries: false },
        display: { theme: 'auto', language: 'fr', units: 'metric' },
        notifications: { push: true, email: true, emailFrequency: 'daily', types: { tripReminders: true, socialActivity: true, achievements: true, planUpdates: true } },
        privacy: {
          profilePublic: false,
          showAvatar: true,
          showBio: true,
          showLocation: false,
          showStats: true,
          allowPublicTrips: true,
          allowLocationTracking: true,
          allowAnalytics: true,
          shareLiveWithFriends: true,
          shareLiveWithClans: true
        },
        security: { twoFactorEnabled: false, twoFactorMethod: null }
      },
      
      // Profile details
      languages: data.languages || [],
      interests: data.interests || [],
      
      // Timestamps
      createdAt: toDateSafe(data.createdAt) || new Date(),
      updatedAt: toDateSafe(data.updatedAt) || new Date(),
      lastLoginAt: toDateSafe(data.lastLoginAt),
      lastActive: toDateSafe(data.lastActive),
      
      // Content counts
      planCount: data.planCount || 0,
      shareCount: data.shareCount || 0,
      flagsCount: data.flagsCount || 0,
      achievementsCount: data.achievementsCount || 0
    } as User
  }

  async getLeaderboard(period: 'week' | 'month' | 'all', limit: number, lastDocId?: string): Promise<LeaderboardEntry[]> {
    logger.debug('Fetching leaderboard', { period, limit, hasLastDoc: !!lastDocId })
    let query: FirestoreQuery = this.firestore.collection('users').orderBy('gamification.points', 'desc').limit(limit) as FirestoreQuery

    // Period filtering based on updatedAt timestamp
    if (period === 'week') {
      const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000
      query = query.where('gamification.updatedAt', '>=', weekAgo)
    } else if (period === 'month') {
      const monthAgo = Date.now() - 30 * 24 * 60 * 60 * 1000
      query = query.where('gamification.updatedAt', '>=', monthAgo)
    }

    // Cursor-based pagination using startAfter
    if (lastDocId) {
      const lastDoc = await this.firestore.collection('users').doc(lastDocId).get()
      if (lastDoc.exists) {
        query = query.startAfter(lastDoc)
      }
    }

    const snapshot = await query.get()
    const leaderboard: LeaderboardEntry[] = snapshot.docs.map((doc: FirestoreDoc, idx: number) => {
      const data = doc.data()
      const gamification = data.gamification || { points: 0, level: 1, badges: [] }
      return {
        userId: doc.id,
        userName: data.displayName || 'Anonymous',
        userAvatar: data.photoURL,
        profileIcon: data.iconSet?.activeIcon,
        profileIconColor: data.iconSet?.accentColor,
        rank: idx + 1,
        points: gamification.points || 0,
        level: gamification.level || 1,
        badges: gamification.badges || [],
        plan: normalizePlanInfo(data),
        status: normalizeStatus(data.status),
        lastActive: toIsoString(data.lastActive || data.lastLoginAt)
      }
    })
    logger.info('Leaderboard fetched', { period, count: leaderboard.length })
    return leaderboard
  }

  async getRecentAchievements(userId: string, limit: number): Promise<Achievement[]> {
    logger.debug('Fetching recent achievements', { userId, limit })
    try {
      const snapshot = await this.firestore
        .collection('achievements')
        .where('userId', '==', userId)
        .orderBy('timestamp', 'desc')
        .limit(limit)
        .get()

      return snapshot.docs.map(
        (doc: FirestoreDoc) => ({ id: doc.id, ...doc.data() } as Achievement)
      )
    } catch (error) {
      // If collection doesn't exist or index is missing, return empty array
      logger.warn('Failed to fetch achievements, returning empty array', { userId, error })
      return []
    }
  }

  async addPoints(userId: string, action: string, amount: number): Promise<void> {
    logger.debug('Adding points to user', { userId, action, amount })
    const batch = createBatchBuilder(this.firestore)
    const now = Date.now()

    // Update user gamification.points
    batch.addUpdate('users', userId, {
      'gamification.points': FieldValue.increment(amount),
      'gamification.updatedAt': now,
      updatedAt: now
    })

    // Create points transaction in same batch
    batch.addSet('pointsTransactions', null, {
      userId,
      action,
      amount,
      timestamp: now
    })

    await batch.commit()
    logger.info('Points added', { userId, action, amount })
  }

  async updateLevel(userId: string, level: number): Promise<void> {
    logger.debug('Updating user level', { userId, level })
    const userRef = this.firestore.collection('users').doc(userId)
    await userRef.update({
      'gamification.level': level,
      'gamification.updatedAt': Date.now()
    })
    logger.info('User level updated', { userId, level })
  }

  async unlockBadge(userId: string, badgeId: BadgeId): Promise<void> {
    logger.debug('Unlocking badge for user', { userId, badgeId })
    const batch = createBatchBuilder(this.firestore)
    const now = Date.now()

    const badge = {
      id: badgeId,
      name: this.getBadgeName(badgeId),
      description: this.getBadgeDescription(badgeId),
      icon: this.getBadgeIcon(badgeId),
      unlockedAt: now
    }

    // Update user gamification.badges
    batch.addUpdate('users', userId, {
      'gamification.badges': FieldValue.arrayUnion(badge),
      'gamification.updatedAt': now,
      updatedAt: now
    })

    // Create achievement in same batch
    batch.addSet('achievements', null, {
      userId,
      type: 'badge',
      title: `Badge Unlocked: ${badge.name}`,
      description: badge.description,
      badgeId,
      createdAt: new Date()
    })

    await batch.commit()
    logger.info('Badge unlocked', { userId, badgeId })
  }

  async createAchievement(data: Omit<Achievement, 'id' | 'createdAt'>): Promise<void> {
    logger.debug('Creating achievement', { userId: data.userId, type: data.type })
    // Remove undefined fields (Achievement has optional: points?, badgeId?, level?, timestamp?)
    const sanitizedData = removeUndefinedFields({
      ...data,
      createdAt: new Date()
    })
    await this.firestore.collection('achievements').add(sanitizedData)
    logger.info('Achievement created', { userId: data.userId, type: data.type })
  }

  private getBadgeName(badgeId: BadgeId): string {
    const names: Record<BadgeId, string> = {
      first_plan: 'First Plan',
      road_warrior: 'Road Warrior',
      explorer: 'Explorer',
      socialite: 'Socialite',
      completionist: 'Completionist',
      collaborator: 'Collaborator',
      early_bird: 'Early Bird',
      globe_trotter: 'Globe Trotter'
    }
    return names[badgeId]
  }

  private getBadgeDescription(badgeId: BadgeId): string {
    const descriptions: Record<BadgeId, string> = {
      first_plan: 'Created your first plan',
      road_warrior: 'Traveled 1000+ km',
      explorer: 'Visited 10 countries',
      socialite: 'Shared 5 plans',
      completionist: 'Completed 10 plans',
      collaborator: 'Collaborated on a plan',
      early_bird: 'Early adopter',
      globe_trotter: 'World traveler'
    }
    return descriptions[badgeId]
  }

  private getBadgeIcon(badgeId: BadgeId): string {
    const icons: Record<BadgeId, string> = {
      first_plan: '🗺️',
      road_warrior: '🏍️',
      explorer: '🌍',
      socialite: '👥',
      completionist: '✅',
      collaborator: '🤝',
      early_bird: '🐦',
      globe_trotter: '✈️'
    }
    return icons[badgeId]
  }
}
